import React, { useState, useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import {
  CSS2DRenderer,
  CSS2DObject,
} from "three/examples/jsm/renderers/CSS2DRenderer";
import PropTypes from "prop-types";
const GlobeVisualization = ({
  locations,
  onLocationSelect,
  searchValue,
  currentTime,
  onEditLocation,
  onDeleteLocation
}) => {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [selectedCountryData, setSelectedCountryData] = useState(null);
  const [selectedCityData, setSelectedCityData] = useState(null);
const [initialized, setInitialized] = useState(false);
  // References to store Three.js objects
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const labelRendererRef = useRef(null);
  const controlsRef = useRef(null);
  const locationParentRef = useRef(null);
  const countryParentRef = useRef(null);

  // Sample data - in a real app you would fetch this

  // Add milestones and risks to each location
  const locationsWithDetails = locations.map((location) => {
    const milestones = [
      {
        id: `${location.id}-ms1`,
        name: `${location.name} Infrastructure Upgrade`,
        description: `Major infrastructure upgrade planned for ${location.name} office`,
        dueDate: "Q3 2023",
        status:
          Math.random() > 0.7
            ? "At Risk"
            : Math.random() > 0.5
            ? "On Track"
            : "Planning",
        statusColor:
          Math.random() > 0.7
            ? "#F59E0B"
            : Math.random() > 0.5
            ? "#10B981"
            : "#3B82F6",
      },
      {
        id: `${location.id}-ms2`,
        name: `${location.name} Team Expansion`,
        description: `Hiring initiative for ${location.name} location`,
        dueDate: "Q4 2023",
        status:
          Math.random() > 0.8
            ? "Delayed"
            : Math.random() > 0.5
            ? "On Track"
            : "Planning",
        statusColor:
          Math.random() > 0.8
            ? "#EF4444"
            : Math.random() > 0.5
            ? "#10B981"
            : "#3B82F6",
      },
    ];

    const risks = [
      {
        id: `${location.id}-risk1`,
        name:
          location.incidents === "red"
            ? "Security Breach"
            : location.incidents === "yellow"
            ? "Resource Shortage"
            : "Compliance Issue",
        description: `Potential impact due to ${
          location.incidents === "red"
            ? "security vulnerabilities"
            : location.incidents === "yellow"
            ? "staffing challenges"
            : "regulatory changes"
        }`,
        severity:
          location.incidents === "red"
            ? "High"
            : location.incidents === "yellow"
            ? "Medium"
            : "Low",
        severityColor:
          location.incidents === "red"
            ? "#EF4444"
            : location.incidents === "yellow"
            ? "#F59E0B"
            : "#10B981",
        impact: "Operations",
        mitigationPlan: `Mitigation strategy in progress for ${location.name}`,
      },
    ];
    let sentiment;
  if (location.incidents === "red") {
    sentiment = "negative";
  } else if (location.incidents === "yellow") {
    sentiment = "neutral";
  } else if (location.incidents === "green") {
    sentiment = "positive";
  } else {
    // Fallback if no incident value
    sentiment = ["positive", "neutral", "negative"][Math.floor(Math.random() * 3)];
  }
    return {
      ...location,
      sentiment,
      milestones,
      risks,
    };
  });

  // Initialize times
  const [times, setTimes] = useState({});

  // Update location times
  useEffect(() => {
    const updateTimes = () => {
      const newTimes = {};
      locationsWithDetails.forEach((location) => {
        if (!location.timezone) return;
  
        try {
          const time = new Date().toLocaleTimeString("en-US", {
            timeZone: location.timezone,
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          newTimes[location.name] = time;
        } catch (e) {
          // Fallback to local time if timezone error
          newTimes[location.name] = new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        }
      });
      setTimes(newTimes);
    };
  
    // Update immediately
    updateTimes();
  
    // Set interval for updates - use 10 seconds instead of 30
    const timer = setInterval(updateTimes, 10000); // Every 10 seconds
  
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [locations]); // Add locations as dependency to re-setup timer when locations change

  // Utility to convert lat/lng to 3D vector
  const latLngToVector3 = (lat, lng, radius) => {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lng + 180) * (Math.PI / 180);

    const x = -(radius * Math.sin(phi) * Math.cos(theta));
    const y = radius * Math.cos(phi);
    const z = radius * Math.sin(phi) * Math.sin(theta);

    return new THREE.Vector3(x, y, z);
  };
 // Complete updated useEffect for initialization
useEffect(() => {
  if (!mountRef.current) return;

  // Scene setup
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x121924);
  sceneRef.current = scene;

  // Renderer setup
  const width = mountRef.current.clientWidth;
  const height = mountRef.current.clientHeight;

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    powerPreference: "high-performance",
  });
  renderer.setSize(width, height);
  renderer.setPixelRatio(window.devicePixelRatio);
  mountRef.current.appendChild(renderer.domElement);
  rendererRef.current = renderer;

  // Camera setup
  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
  camera.position.z = 7;
  cameraRef.current = camera;

  // CSS2D renderer for labels
  const labelRenderer = new CSS2DRenderer();
  labelRenderer.setSize(width, height);
  labelRenderer.domElement.style.position = "absolute";
  labelRenderer.domElement.style.top = "0";
  labelRenderer.domElement.style.pointerEvents = "none";
  mountRef.current.appendChild(labelRenderer.domElement);
  labelRendererRef.current = labelRenderer;

  // Lighting
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
  directionalLight.position.set(1, 1, 1);
  scene.add(directionalLight);

  // Orbit controls
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.15;
  controls.rotateSpeed = 0.5;
  controls.minDistance = 5;
  controls.maxDistance = 12;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.3;
  controls.enablePan = false;
  controlsRef.current = controls;

  // Add hemisphere constraints to prevent upside-down view
  controls.minPolarAngle = Math.PI * 0.1;
  controls.maxPolarAngle = Math.PI * 0.9;

  // Create starfield
  const starGeometry = new THREE.BufferGeometry();
  const starVertices = [];

  for (let i = 0; i < 5000; i++) {
    const x = THREE.MathUtils.randFloatSpread(2000);
    const y = THREE.MathUtils.randFloatSpread(2000);
    const z = THREE.MathUtils.randFloatSpread(2000);
    starVertices.push(x, y, z);
  }

  starGeometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(starVertices, 3)
  );
  const starMaterial = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 0.5,
    transparent: true,
    opacity: 0.8,
    sizeAttenuation: true,
  });
  const stars = new THREE.Points(starGeometry, starMaterial);
  scene.add(stars);

  // Create parent groups for countries and locations
  const countryParent = new THREE.Group();
  scene.add(countryParent);
  countryParentRef.current = countryParent;
  // console.log("Added countryParent to scene:", scene.children.includes(countryParent));

  const locationParent = new THREE.Group();
  scene.add(locationParent);
  locationParentRef.current = locationParent;
  // console.log("Added locationParent to scene:", scene.children.includes(locationParent));

  // Load Earth texture and create globe
  const earthTextureUrl =
    "https://cdn.jsdelivr.net/npm/three-globe/example/img/earth-blue-marble.jpg";
  const earthBumpMapUrl =
    "https://cdn.jsdelivr.net/npm/three-globe/example/img/earth-topology.png";
  const earthSpecularMapUrl =
    "https://cdn.jsdelivr.net/npm/three-globe/example/img/earth-water.png";

  // Track loading progress
  let loadedCount = 0;
  const textureLoader = new THREE.TextureLoader();
  textureLoader.crossOrigin = "anonymous";

  // Create the Earth sphere with default material first
  const earthGeometry = new THREE.SphereGeometry(4, 64, 64);
  const earthMaterial = new THREE.MeshPhongMaterial({
    color: 0x2233aa, // Default blue color if texture fails
    shininess: 15,
    reflectivity: 0.2,
  });

  const earthGroup = new THREE.Group();
  const earthMesh = new THREE.Mesh(earthGeometry, earthMaterial);
  earthGroup.add(earthMesh);

  // Add atmosphere glow effect
  const glowGeometry = new THREE.SphereGeometry(4.15, 64, 64);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: 0x0077ff,
    transparent: true,
    opacity: 0.08,
    side: THREE.BackSide,
  });

  const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
  earthGroup.add(glowMesh);

  // Add to scene immediately
  scene.add(earthGroup);

  // Start animation loop immediately
  const animate = () => {
    if (controlsRef.current) controlsRef.current.update();

    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    if (labelRendererRef.current && sceneRef.current && cameraRef.current) {
      labelRendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    requestAnimationFrame(animate);
  };

  animate();
  setInitialized(true);
  
  // IMPORTANT: Create country markers immediately - don't wait for textures
  // This ensures labels are visible on first load
  // console.log("Creating initial country markers");
  createCountryMarkers();
  createLocationMarkers();

  // Force completion after 5 seconds if textures are taking too long
  const forceCompletion = () => {
    if (loading) {
      // console.warn("Globe loading timed out - forcing display");
      setLoading(false);

      // Refresh the markers to ensure they're displayed
      createCountryMarkers();
      createLocationMarkers();
    }
  };

  // Set a timeout for forced completion
  const forceTimer = setTimeout(forceCompletion, 5000);
  
  // Set a timeout to ensure markers are created after scene has fully initialized
  const initTimer = setTimeout(() => {
    // console.log("Delayed initialization check");
    if (countryParentRef.current && countryParentRef.current.children.length === 0) {
      // console.log("No markers found after initialization, forcing marker creation");
      createCountryMarkers();
      createLocationMarkers();
    }
  }, 1000);

  // Try to load textures to enhance the globe
  textureLoader.load(
    earthTextureUrl,
    (texture) => {
      // Apply the texture to the material
      earthMaterial.map = texture;
      earthMaterial.needsUpdate = true;

      // Increment loaded count and check
      loadedCount++;
      if (loadedCount >= 3) {
        clearTimeout(forceTimer);
        setLoading(false);
        // Refresh country markers when textures are loaded
        createCountryMarkers();
        createLocationMarkers();
      }

      // Try to load bump map
      textureLoader.load(
        earthBumpMapUrl,
        (bumpMap) => {
          earthMaterial.bumpMap = bumpMap;
          earthMaterial.bumpScale = 0.05;
          earthMaterial.needsUpdate = true;

          loadedCount++;
          if (loadedCount >= 3) {
            clearTimeout(forceTimer);
            setLoading(false);
            // Refresh country markers when textures are loaded
            createCountryMarkers();
            createLocationMarkers();
          }

          // Try to load specular map
          textureLoader.load(
            earthSpecularMapUrl,
            (specularMap) => {
              earthMaterial.specularMap = specularMap;
              earthMaterial.needsUpdate = true;

              loadedCount++;
              if (loadedCount >= 3) {
                clearTimeout(forceTimer);
                setLoading(false);
                // Refresh country markers when textures are loaded
                createCountryMarkers();
                createLocationMarkers();
              }

              // Try to load clouds as a bonus
              textureLoader.load(
                "https://cdn.jsdelivr.net/npm/three-globe/example/img/earth-clouds.png",
                (cloudsTexture) => {
                  const cloudsGeometry = new THREE.SphereGeometry(
                    4.05,
                    64,
                    64
                  );
                  const cloudsMaterial = new THREE.MeshPhongMaterial({
                    map: cloudsTexture,
                    transparent: true,
                    opacity: 0.3,
                  });

                  const cloudsMesh = new THREE.Mesh(
                    cloudsGeometry,
                    cloudsMaterial
                  );
                  earthGroup.add(cloudsMesh);

                  // Animate clouds
                  const animateClouds = () => {
                    if (cloudsMesh) {
                      cloudsMesh.rotation.y += 0.0002;
                      requestAnimationFrame(animateClouds);
                    }
                  };

                  animateClouds();
                }
              );
            },
            undefined,
            () => {
              // Error handler for specular map
              console.warn("Failed to load specular map");
              loadedCount++;
              if (loadedCount >= 3) {
                clearTimeout(forceTimer);
                setLoading(false);
                // Refresh country markers even if specular map fails
                createCountryMarkers();
                createLocationMarkers();
              }
            }
          );
        },
        undefined,
        () => {
          // Error handler for bump map
          console.warn("Failed to load bump map");
          loadedCount++;
          if (loadedCount >= 3) {
            clearTimeout(forceTimer);
            setLoading(false);
            // Refresh country markers even if bump map fails
            createCountryMarkers();
            createLocationMarkers();
          }
        }
      );
    },
    undefined,
    () => {
      // Error handler for main texture
      console.warn("Failed to load earth texture");
      loadedCount++;
      if (loadedCount >= 3) {
        clearTimeout(forceTimer);
        setLoading(false);
        // Refresh country markers even if main texture fails
        createCountryMarkers();
        createLocationMarkers();
      }
    }
  );

  // Window resize handler
  const handleResize = () => {
    if (!mountRef.current || !cameraRef.current || !rendererRef.current)
      return;

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    cameraRef.current.aspect = width / height;
    cameraRef.current.updateProjectionMatrix();

    rendererRef.current.setSize(width, height);

    if (labelRendererRef.current) {
      labelRendererRef.current.setSize(width, height);
    }
  };

  window.addEventListener("resize", handleResize);

  // Cleanup
  return () => {
    window.removeEventListener("resize", handleResize);
    clearTimeout(forceTimer);
    clearTimeout(initTimer); // Clear the new timer

    if (rendererRef.current && mountRef.current) {
      mountRef.current.removeChild(rendererRef.current.domElement);
    }

    if (labelRendererRef.current && mountRef.current) {
      mountRef.current.removeChild(labelRendererRef.current.domElement);
    }
  };
}, []);

// Add this new useEffect for locations monitoring
// Updated useEffect to handle location changes more dynamically
useEffect(() => {
  if (locations && locations.length > 0) {
    // Ensure markers are recreated when locations change
    if (initialized && countryParentRef.current && sceneRef.current) {
      // Recreate both country and location markers
      createCountryMarkers();
      createLocationMarkers();
    }

    // Update times for each location
    const newTimes = {};
    locations.forEach((location) => {
      if (!location.timezone) return;

      try {
        const time = new Date().toLocaleTimeString("en-US", {
          timeZone: location.timezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        newTimes[location.name] = time;
      } catch (e) {
        // Fallback to local time if timezone error
        newTimes[location.name] = new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
    });
    setTimes(newTimes);
  }
}, [locations, initialized]);

// Enhanced createCountryMarkers to handle dynamic updates
const createCountryMarkers = () => {
  if (!countryParentRef.current || !sceneRef.current) return;

  // Clear existing country markers
  while (countryParentRef.current.children.length > 0) {
    countryParentRef.current.remove(countryParentRef.current.children[0]);
  }

  // Group locations by country
  const countriesWithLocations = {};

  locations.forEach((location) => {
    if (location.countryCode) {
      if (!countriesWithLocations[location.countryCode]) {
        countriesWithLocations[location.countryCode] = {
          code: location.countryCode,
          name: location.country || "Unknown Country",
          flag: location.flag || "🌐",
          cities: [],
          centroid: { lat: 0, lng: 0 },
        };
      }

      // Add city to the country
      countriesWithLocations[location.countryCode].cities.push({
        id: location.id,
        name: location.name,
        lat: location.lat,
        lng: location.lng,
        details: location.details || {},
        incidents: location.incidents,
        timezone: location.timezone,
        timezoneName: location.timezoneName,
        milestones: location.milestones || [],
        risks: location.risks || [],
      });
    }
  });

  // Calculate proper centroids
  Object.values(countriesWithLocations).forEach((country) => {
    if (country.cities.length > 0) {
      // Reset centroid
      country.centroid = { lat: 0, lng: 0 };

      // Sum all coordinates
      country.cities.forEach((city) => {
        country.centroid.lat += city.lat;
        country.centroid.lng += city.lng;
      });

      // Divide by count
      country.centroid.lat /= country.cities.length;
      country.centroid.lng /= country.cities.length;
    }
  });

  // Add country markers
  Object.values(countriesWithLocations).forEach((country) => {
    // Skip countries with no cities
    if (country.cities.length === 0) return;

    // Create a marker for the country
    const position = latLngToVector3(
      country.centroid.lat,
      country.centroid.lng,
      4.1
    );

    // Create the label element
    const countryDiv = document.createElement("div");
    countryDiv.className = "country-label";
    countryDiv.style.backgroundColor = "rgba(16, 24, 40, 0.85)";
    countryDiv.style.color = "white";
    countryDiv.style.padding = "5px 10px";
    countryDiv.style.borderRadius = "6px";
    countryDiv.style.fontSize = "14px";
    countryDiv.style.fontWeight = "bold";
    countryDiv.style.border = "1px solid rgba(59, 130, 246, 0.5)";
    countryDiv.style.display = "flex";
    countryDiv.style.flexDirection = "column";
    countryDiv.style.alignItems = "center";
    countryDiv.style.gap = "5px";
    countryDiv.style.pointerEvents = "auto";
    countryDiv.style.cursor = "pointer";
    countryDiv.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.3)";
    countryDiv.style.backdropFilter = "blur(4px)";

    // Flag and name
    const flagSpan = document.createElement("span");
    flagSpan.textContent = country.flag;
    flagSpan.style.fontSize = "18px";

    const nameSpan = document.createElement("span");
    nameSpan.textContent = country.name;

    // City count pill
    const countSpan = document.createElement("span");
    countSpan.textContent = country.cities.length;
    countSpan.style.backgroundColor = "rgba(59, 130, 246, 0.3)";
    countSpan.style.color = "#90cdf4";
    countSpan.style.borderRadius = "10px";
    countSpan.style.padding = "2px 6px";
    countSpan.style.fontSize = "12px";
    countSpan.style.fontWeight = "bold";

    // Create the container
    const infoRow = document.createElement("div");
    infoRow.style.display = "flex";
    infoRow.style.alignItems = "center";
    infoRow.style.gap = "5px";
    infoRow.style.width = "100%";

    // Add elements to infoRow
    infoRow.appendChild(flagSpan);
    infoRow.appendChild(nameSpan);
    infoRow.appendChild(countSpan);

    // Add infoRow to countryDiv
    countryDiv.appendChild(infoRow);

    // Time display
    const timeDisplay = document.createElement("div");
    timeDisplay.style.fontSize = "11px";
    timeDisplay.style.marginTop = "3px";
    timeDisplay.style.color = "#00e5ff";
    timeDisplay.textContent = times[country.cities[0].name] || "--:--";
    countryDiv.appendChild(timeDisplay);

    // Create the CSS2D object
    const countryLabel = new CSS2DObject(countryDiv);
    countryLabel.position.copy(position);

    // Store data in userData
    countryLabel.userData = {
      position: position.clone(),
      country: country,
    };

    // Handle click to focus on country
    countryDiv.addEventListener("click", () => {
      if (selectedCountry === country.code) {
        // If already selected, deselect
        setSelectedCountry(null);
        setSelectedCountryData(null);
        setShowSidePanel(false);

        // Resume auto-rotation
        if (controlsRef.current) {
          controlsRef.current.autoRotate = true;
          setAutoRotate(true);
        }

        // Remove all city markers when deselecting a country
        while (locationParentRef.current.children.length > 0) {
          locationParentRef.current.remove(
            locationParentRef.current.children[0]
          );
        }

        // Remove any country and city name displays
        const nameDisplays = document.querySelectorAll(
          ".country-name-display, .city-name-display"
        );
        nameDisplays.forEach((display) => display.remove());
      } else {
        // Select this country
        setSelectedCountry(country.code);
        setSelectedCountryData(country);
        setSelectedCityData(null);
        setShowSidePanel(true);

        // Stop auto-rotation
        if (controlsRef.current) {
          controlsRef.current.autoRotate = false;
          setAutoRotate(false);
        }

        // Focus camera on country position with slightly closer zoom
        focusCameraOnPosition(position, 1.5);

        // Recreate location markers
        setTimeout(() => {
          createLocationMarkers();
        }, 50);
      }
    });

    // Add to the parent group
    countryParentRef.current.add(countryLabel);
  });
};


// Post-initialization effect
useEffect(() => {
  if (initialized && countryParentRef.current && sceneRef.current) {
    // console.log("Running post-initialization effect to create country markers");
    // Force creation of country markers after initialization
    createCountryMarkers();
    // Also create any location markers if a country is selected
    if (selectedCountry) {
      createLocationMarkers();
    }
  }
}, [initialized,currentTime]);
// Add this near your other useEffect hooks
useEffect(() => {
  // This will run whenever currentTime changes
  if (Object.keys(currentTime).length > 0) {
    // Get all time displays
    const timeDisplays = document.querySelectorAll("[data-city-name]");
    
    // Update each time display with current time
    timeDisplays.forEach(display => {
      const cityName = display.dataset.cityName;
      if (currentTime[cityName]) {
        display.textContent = currentTime[cityName];
      }
    });
  }
}, [currentTime]);

// Complete updated createCountryMarkers function

// Update these sections in your code to fix the country visibility issue

// 1. Add a new useEffect to ensure country markers are created after initialization

  
  // Function to create location markers
  // Function to create location markers - updated to only show city names when a country is selected
  // Enhanced function to create location markers with better positioning
 // Enhanced createLocationMarkers to handle dynamic updates
const createLocationMarkers = () => {
  if (!locationParentRef.current || !sceneRef.current) return;

  // Clear existing location markers
  while (locationParentRef.current.children.length > 0) {
    locationParentRef.current.remove(locationParentRef.current.children[0]);
  }

  // Clear existing city labels from the scene
  sceneRef.current.children.forEach((child) => {
    if (
      child.isCSS2DObject &&
      child.element &&
      child.element.className === "city-label"
    ) {
      sceneRef.current.remove(child);
    }
  });

  // Remove any existing city name displays if no city is selected
  if (!selectedCityData) {
    const cityLabels = document.querySelectorAll(".city-name-display");
    cityLabels.forEach((label) => label.remove());
  }

  // Only proceed with creating city markers if a country is selected
  if (!selectedCountry) return;

  // Add new location markers
  locations.forEach((location) => {
    if (!location.lat || !location.lng) return;

    // Skip if this location isn't in the selected country
    if (location.countryCode !== selectedCountry) return;

    // Convert lat/lng to 3D coordinates - ensure it's on the globe surface
    const position = latLngToVector3(location.lat, location.lng, 4);

    // Create a marker group for this location
    const markerGroup = new THREE.Group();
    markerGroup.position.copy(position);

    // Adjust marker orientation to face outward from the globe center
    markerGroup.lookAt(0, 0, 0);
    markerGroup.rotateX(Math.PI / 2);

    // Determine marker color based on incident status
    let markerColor = 0x3b82f6; // Default blue
    if (location.incidents === "red") markerColor = 0xef4444;
    else if (location.incidents === "yellow") markerColor = 0xf59e0b;
    else if (location.incidents === "green") markerColor = 0x10b981;

    // Create the marker dot with increased size for visibility
    const dotGeometry = new THREE.SphereGeometry(0.05, 16, 16);
    const dotMaterial = new THREE.MeshBasicMaterial({ color: markerColor });
    const dot = new THREE.Mesh(dotGeometry, dotMaterial);

    // Create a pulsing ring effect
    const ringGeometry = new THREE.RingGeometry(0.06, 0.09, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: markerColor,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);

    // Animation for the ring
    ring.userData.update = () => {
      const scale = 1 + 0.3 * Math.sin(Date.now() * 0.003);
      ring.scale.set(scale, scale, scale);
    };

    // Add update function to animation loop
    const updateRing = () => {
      ring.userData.update();
      requestAnimationFrame(updateRing);
    };
    requestAnimationFrame(updateRing);

    // Add marker components to the group
    markerGroup.add(dot);
    markerGroup.add(ring);

    // Create a slightly larger invisible sphere for better mouse interaction
    const hitTestGeometry = new THREE.SphereGeometry(0.15, 16, 16);
    const hitTestMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
    });
    const hitTest = new THREE.Mesh(hitTestGeometry, hitTestMaterial);
    hitTest.userData.location = location;
    markerGroup.add(hitTest);

    // Create city label with clean formatting
    const cityDiv = document.createElement("div");
    cityDiv.className = "city-label";
    cityDiv.style.backgroundColor = "rgba(16, 24, 40, 0.85)";
    cityDiv.style.color = "white";
    cityDiv.style.padding = "4px 8px";
    cityDiv.style.borderRadius = "4px";
    cityDiv.style.fontSize = "12px";
    cityDiv.style.fontWeight = "bold";
    cityDiv.style.border = "1px solid rgba(59, 130, 246, 0.5)";
    cityDiv.style.pointerEvents = "auto";
    cityDiv.style.cursor = "pointer";
    cityDiv.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.3)";
    cityDiv.style.backdropFilter = "blur(4px)";
    cityDiv.style.zIndex = "1000";

    // Add status indicator dot
    const statusDot = document.createElement("span");
    statusDot.style.display = "inline-block";
    statusDot.style.width = "6px";
    statusDot.style.height = "6px";
    statusDot.style.borderRadius = "50%";
    statusDot.style.marginRight = "5px";

    // Set the dot color based on incident status
    if (location.incidents === "red")
      statusDot.style.backgroundColor = "#ef4444";
    else if (location.incidents === "yellow")
      statusDot.style.backgroundColor = "#f59e0b";
    else if (location.incidents === "green")
      statusDot.style.backgroundColor = "#10b981";
    else statusDot.style.backgroundColor = "#3b82f6";

    cityDiv.appendChild(statusDot);

    // Add city name - proper capitalization
    const nameSpan = document.createElement("span");
    const formattedName = location.name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    nameSpan.textContent = formattedName;
    cityDiv.appendChild(nameSpan);

    // Create the CSS2D object for the city label - slightly offset position
    const cityLabel = new CSS2DObject(cityDiv);
    // Position label slightly above the marker for better visibility
    const labelPosition = position.clone().normalize().multiplyScalar(4.2);
    cityLabel.position.copy(labelPosition);

    // Handle city click
    cityDiv.addEventListener("click", () => {
      showLocationDetails(location);
    });

    // Add label to scene
    sceneRef.current.add(cityLabel);

    // Add the marker group to the parent
    locationParentRef.current.add(markerGroup);
  });

  // Set up raycasting for location hover/click
  setupLocationInteraction();
};

  // Function to focus camera on a position
  // Enhanced focusCameraOnPosition function
  // Improved camera focus function for better position accuracy
 
  // Function to set up location interaction
  const setupLocationInteraction = () => {
    if (!rendererRef.current || !cameraRef.current || !sceneRef.current) return;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Get all hitTest meshes
    const getHitTestMeshes = () => {
      const meshes = [];
      if (locationParentRef.current) {
        locationParentRef.current.traverse((child) => {
          if (child.userData && child.userData.location) {
            meshes.push(child);
          }
        });
      }
      return meshes;
    };

    // Function to check if we're hovering over a location
    const onMouseMove = (event) => {
      // Calculate mouse position in normalized device coordinates
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update the picking ray with the camera and mouse position
      raycaster.setFromCamera(mouse, cameraRef.current);

      // Get all hit test meshes
      const hitTestMeshes = getHitTestMeshes();

      // Calculate objects intersecting the picking ray
      const intersects = raycaster.intersectObjects(hitTestMeshes);

      if (intersects.length > 0) {
        const location = intersects[0].object.userData.location;
        if (location) {
          setHoveredLocation(location);
          document.body.style.cursor = "pointer";
        }
      } else {
        setHoveredLocation(null);
        document.body.style.cursor = "default";
      }
    };

    // Function to handle clicks on locations
    const onClick = (event) => {
      // Calculate mouse position in normalized device coordinates
      const rect = rendererRef.current.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update the picking ray with the camera and mouse position
      raycaster.setFromCamera(mouse, cameraRef.current);

      // Get all hit test meshes
      const hitTestMeshes = getHitTestMeshes();

      // Calculate objects intersecting the picking ray
      const intersects = raycaster.intersectObjects(hitTestMeshes);

      if (intersects.length > 0) {
        const location = intersects[0].object.userData.location;
        if (location) {
          showLocationDetails(location);
        }
      }
    };

    // Add event listeners
    rendererRef.current.domElement.addEventListener("mousemove", onMouseMove);
    rendererRef.current.domElement.addEventListener("click", onClick);

    // Return cleanup function
    return () => {
      if (rendererRef.current) {
        rendererRef.current.domElement.removeEventListener(
          "mousemove",
          onMouseMove
        );
        rendererRef.current.domElement.removeEventListener("click", onClick);
      }
    };
  };

  // Function to show location details
  // In your showLocationDetails function:
  // Function to show location details
  // Improved function to show location details with accurate positioning
  const showLocationDetails = (location) => {
    // First find the matching location in your structured data
    const matchedLocation = locationsWithDetails.find(
      (loc) =>
        loc.id === location.id ||
        loc.name.toLowerCase() === location.name.toLowerCase()
    );

    if (!matchedLocation) return;

    // Use this for precise focusing exactly on the city position
    const position = latLngToVector3(
      matchedLocation.lat,
      matchedLocation.lng,
      4
    );

    // More accurate focus on position with slightly closer zoom
    // Use a closer view for cities to see more detail
    focusCameraOnPosition(position.clone().multiplyScalar(0.95), 1.5);

    if (controlsRef.current) {
      controlsRef.current.autoRotate = false;
      setAutoRotate(false);
    }

    setSelectedCityData(matchedLocation);
    setShowSidePanel(true);

    setHoveredLocation(matchedLocation);
    setTimeout(() => {
      if (hoveredLocation && hoveredLocation.id === matchedLocation.id) {
        setHoveredLocation(null);
      }
    }, 2000);

    // Log the selected city for debugging
    // console.log(
    //   `Selected city: ${matchedLocation.name} in ${matchedLocation.country} at position:`,
    //   `lat: ${matchedLocation.lat}, lng: ${matchedLocation.lng}`
    // );

    // Recreate the location markers to show the selected city highlight
    setTimeout(() => {
      createLocationMarkers();
    }, 50);
  };
  // Function to close the side panel
  // Updated handleResetView function with improved camera reset
  const handleResetView = () => {
    // console.log("Reset View initiated");
    
    // Immediate actions
    setSelectedCountry(null);
    setSelectedCountryData(null);
    setSelectedCityData(null);
    setShowSidePanel(false);
  
    // Remove any name displays
    const nameDisplays = document.querySelectorAll(
      ".country-name-display, .city-name-display"
    );
    nameDisplays.forEach((display) => display.remove());
  
    // Ensure controls exist before manipulation
    if (controlsRef.current) {
      // Stop any ongoing animations
      controlsRef.current.enabled = false;
  
      // Reset camera position
      if (cameraRef.current) {
        cameraRef.current.position.set(0, 0, 7);
        cameraRef.current.lookAt(0, 0, 0);
      }
  
      // Reset controls target
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
  
      // Restart auto-rotation
      controlsRef.current.autoRotate = true;
      controlsRef.current.autoRotateSpeed = 0.3;
      setAutoRotate(true);
  
      // Re-enable controls
      controlsRef.current.enabled = true;
    }
  
    // Clear location markers
    if (locationParentRef.current) {
      while (locationParentRef.current.children.length > 0) {
        locationParentRef.current.remove(locationParentRef.current.children[0]);
      }
    }
  
    // Recreate country markers
    createCountryMarkers();
  
    // Force render update
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  
    if (labelRendererRef.current && sceneRef.current && cameraRef.current) {
      labelRendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  
    // console.log("Reset view completed");
  };
// Modify the focusCameraOnPosition function to handle the special case of resetting to center
// Add this code to your existing focusCameraOnPosition function
const focusCameraOnPosition = (position, duration = 1) => {
  if (!cameraRef.current || !controlsRef.current) return;

  // Special case for reset to center (when position is at origin)
  const isResetToCenter = 
    position.x === 0 && position.y === 0 && position.z === 0;
  
  // For reset to center, use direct camera position
  if (isResetToCenter) {
    // console.log("Resetting camera to center view");
    // Target position for the camera (further away for full globe view)
    const targetPosition = new THREE.Vector3(0, 0, 7);
    
    // Disable controls during transition
    controlsRef.current.enabled = false;
    
    // Get current camera position
    const startPosition = cameraRef.current.position.clone();
    const startLookAt = controlsRef.current.target.clone();
    const startTime = Date.now();
    
    // Animation function specifically for center reset
    const animateResetCamera = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const t = Math.min(elapsed / duration, 1.0);
      const easeT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // Ease in-out quad
      
      // Interpolate position
      cameraRef.current.position
        .copy(startPosition)
        .lerp(targetPosition, easeT);
      
      // Interpolate controls target back to origin
      const originTarget = new THREE.Vector3(0, 0, 0);
      controlsRef.current.target.copy(startLookAt).lerp(originTarget, easeT);
      
      // Look at the interpolated target
      cameraRef.current.lookAt(controlsRef.current.target);
      
      if (t < 1.0) {
        requestAnimationFrame(animateResetCamera);
      } else {
        // Re-enable controls once animation completes
        controlsRef.current.enabled = true;
        // Ensure we're completely reset
        cameraRef.current.position.set(0, 0, 7);
        controlsRef.current.target.set(0, 0, 0);
        controlsRef.current.update();
        
        // console.log("Camera reset to center complete");
      }
    };
    
    animateResetCamera();
    return;
  }
  
  // Regular position focusing (your existing code)
  // Calculate the target direction - adjusted for better positioning
  const targetPosition = position.clone().normalize().multiplyScalar(5.5);
  
  // Update the controls target to exactly match the position vector
  const targetLookAt = position.clone().normalize().multiplyScalar(4);
  
  // Disable controls during transition
  controlsRef.current.enabled = false;
  
  // Get current camera position
  const startPosition = cameraRef.current.position.clone();
  const startLookAt = controlsRef.current.target.clone();
  const startTime = Date.now();
  
  // Animation function
  const animateCamera = () => {
    const elapsed = (Date.now() - startTime) / 1000;
    const t = Math.min(elapsed / duration, 1.0);
    const easeT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // Ease in-out quad
    
    // Interpolate position
    cameraRef.current.position
      .copy(startPosition)
      .lerp(targetPosition, easeT);
    
    // Interpolate controls target for better orbiting
    controlsRef.current.target.copy(startLookAt).lerp(targetLookAt, easeT);
    
    // Look at the interpolated target
    cameraRef.current.lookAt(controlsRef.current.target);
    
    if (t < 1.0) {
      requestAnimationFrame(animateCamera);
    } else {
      // Re-enable controls once animation completes
      controlsRef.current.enabled = true;
      controlsRef.current.update();
      
      // console.log(
      //   `Camera focused on position: ${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}`
      // );
    }
  };
  
  animateCamera();
};

  // Function to close the side panel - updated to handle back button properly
  const handleCloseSidePanel = () => {
    setShowSidePanel(false);

    // If a city is selected, remove the city selection but keep the country selection
    if (selectedCityData) {
      setSelectedCityData(null);

      // Remove any city name displays
      const cityLabels = document.querySelectorAll(".city-name-display");
      cityLabels.forEach((label) => label.remove());

      // If we have a country selected, focus back on the country
      if (selectedCountryData) {
        const position = latLngToVector3(
          selectedCountryData.centroid.lat,
          selectedCountryData.centroid.lng,
          4.1
        );
        focusCameraOnPosition(position, 1.0);
      }

      // Recreate location markers to refresh the view without the selected city
      setTimeout(() => {
        createLocationMarkers();
      }, 50);
    } else {
      // If no city is selected, this is a "back to globe" request
      // Same as reset view
      handleResetView();
    }

    // Force render
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    if (labelRendererRef.current && sceneRef.current && cameraRef.current) {
      labelRendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  };
  // Get the status color based on the incident level
  const getStatusColor = (incidents) => {
    if (incidents === "red") return "#ef4444";
    if (incidents === "yellow") return "#f59e0b";
    if (incidents === "green") return "#10b981";
    return "#3b82f6"; // Default blue
  };

  // Get the status text based on the incident level
  const getStatusText = (incidents) => {
    if (incidents === "red") return "High Alert";
    if (incidents === "yellow") return "Medium Alert";
    if (incidents === "green") return "Normal Status";
    return "Status Unknown";
  };
  // Function to close the side panel
  const handleCloseSidePanel1 = () => {
    setShowSidePanel(false);
    setSelectedCountry(null);
    setSelectedCountryData(null);
    setSelectedCityData(null);

    // Reset the camera view
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(0, 0, 7);
      cameraRef.current.lookAt(0, 0, 0);
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();

      // Restart auto-rotation
      controlsRef.current.autoRotate = true;
      setAutoRotate(true);

      // Recreate all markers
      createCountryMarkers();
      createLocationMarkers();
    }
  };



  // 1. Add state variables for search
// Add these to your existing state variables at the top of the component
const [searchQuery, setSearchQuery] = useState("");
const [searchResults, setSearchResults] = useState([]);
const [showSearchResults, setShowSearchResults] = useState(false);

// 2. Add a search function to filter countries and locations
// Add this function to your component
const handleSearch = (query) => {
  setSearchQuery(query);
  
  if (!query.trim()) {
    setSearchResults([]);
    setShowSearchResults(false);
    return;
  }
  
  const lowerQuery = query.toLowerCase().trim();
  
  // First check for country matches
  const countryMatches = [];
  const cityMatches = [];
  
  // Create a map to track which countries we've already added
  const addedCountries = new Set();
  
  // Search through all locations
  locationsWithDetails.forEach(location => {
    // Check if this is a country match
    if (
      location.country && 
      location.country.toLowerCase().includes(lowerQuery) &&
      !addedCountries.has(location.countryCode)
    ) {
      countryMatches.push({
        id: location.countryCode,
        name: location.country,
        type: 'country',
        flag: location.flag || '🌐',
        countryCode: location.countryCode
      });
      addedCountries.add(location.countryCode);
    }
    
    // Check if this is a city/location match
    if (location.name.toLowerCase().includes(lowerQuery)) {
      cityMatches.push({
        id: location.id,
        name: location.name,
        type: 'city',
        country: location.country,
        flag: location.flag || '🌐',
        countryCode: location.countryCode,
        incidents: location.incidents,
        location: location
      });
    }
  });
  
  // Combine results with countries first
  const combinedResults = [...countryMatches, ...cityMatches];
  
  setSearchResults(combinedResults);
  setShowSearchResults(combinedResults.length > 0);
};

// 3. Create a function to handle clicking on search results
const handleSearchResultClick = (result) => {
  setSearchQuery("");
  setShowSearchResults(false);
  
  if (result.type === 'country') {
    // Find a sample location from this country to use for centering
    const countryLocations = locationsWithDetails.filter(
      loc => loc.countryCode === result.countryCode
    );
    
    if (countryLocations.length > 0) {
      // Calculate the centroid of all locations in this country
      const avgLat = countryLocations.reduce((sum, loc) => sum + loc.lat, 0) / countryLocations.length;
      const avgLng = countryLocations.reduce((sum, loc) => sum + loc.lng, 0) / countryLocations.length;
      
      // Create a position from this centroid
      const position = latLngToVector3(avgLat, avgLng, 4.1);
      
      // Focus camera on this position
      focusCameraOnPosition(position, 1.5);
      
      // Create country data and select it
      const countryData = {
        code: result.countryCode,
        name: result.name,
        flag: result.flag,
        cities: countryLocations.map(loc => ({
          id: loc.id,
          name: loc.name,
          lat: loc.lat,
          lng: loc.lng,
          details: loc.details || {},
          incidents: loc.incidents,
          timezone: loc.timezone,
          timezoneName: loc.timezoneName,
          milestones: loc.milestones || [],
          risks: loc.risks || [],
        })),
        centroid: { lat: avgLat, lng: avgLng }
      };
      
      // Select this country
      setSelectedCountry(result.countryCode);
      setSelectedCountryData(countryData);
      setSelectedCityData(null);
      setShowSidePanel(true);
      
      // Stop auto-rotation
      if (controlsRef.current) {
        controlsRef.current.autoRotate = false;
        setAutoRotate(false);
      }
      
      // Update location markers
      setTimeout(() => {
        createLocationMarkers();
      }, 50);
    }
  } else if (result.type === 'city') {
    // It's a city, so show its details
    showLocationDetails(result.location);
  }
};
useEffect(() => {
  const handleClickOutside = (event) => {
    if (showSearchResults) {
      // Check if the click is outside the search bar and results
      const searchContainer = document.getElementById('search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowSearchResults(false);
      }
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [showSearchResults]);



const getSentimentColor = (sentiment) => {
  if (sentiment === "positive") return "#10b981"; // Green
  if (sentiment === "neutral") return "#3b82f6"; // Blue
  if (sentiment === "negative") return "#ef4444"; // Red
  return "#3b82f6"; // Default blue
};

// 3. Add a function to get sentiment text
// Add this function near other utility functions (around line 2100)
const getSentimentText = (sentiment) => {
  if (sentiment === "positive") return "Positive";
  if (sentiment === "neutral") return "Neutral";
  if (sentiment === "negative") return "Negative";
  return "Unknown";
};
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "600px",
      }}
    >
      <div
  style={{
    position: "absolute",
    left: "50%",
    top: "20px",
    transform: "translateX(-50%)",
    zIndex: 100,
    width: "300px",
  }}
>
  <div
    style={{
      backgroundColor: "rgba(16, 24, 40, 0.85)",
      borderRadius: "8px",
      padding: "10px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
      backdropFilter: "blur(8px)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        borderRadius: "6px",
        padding: "8px 12px",
        border: "1px solid rgba(59, 130, 246, 0.3)",
      }}
    >
      <span style={{ color: "rgba(255, 255, 255, 0.6)", marginRight: "8px" }}>
        🔍
      </span>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder="Search countries or locations..."
        style={{
          background: "transparent",
          border: "none",
          outline: "none",
          color: "white",
          width: "100%",
          fontSize: "14px",
          fontFamily: "inherit",
        }}
      />
      {searchQuery && (
        <button
          onClick={() => {
            setSearchQuery("");
            setShowSearchResults(false);
          }}
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(255, 255, 255, 0.6)",
            cursor: "pointer",
            fontSize: "16px",
            padding: "0",
            marginLeft: "8px",
          }}
        >
          ✕
        </button>
      )}
    </div>
    
    {showSearchResults && (
      <div
        style={{
          backgroundColor: "rgba(16, 24, 40, 0.95)",
          borderRadius: "6px",
          marginTop: "8px",
          maxHeight: "300px",
          overflowY: "auto",
          border: "1px solid rgba(59, 130, 246, 0.3)",
        }}
      >
        {searchResults.length === 0 ? (
          <div
            style={{
              padding: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              textAlign: "center",
            }}
          >
            No results found
          </div>
        ) : (
          searchResults.map((result) => (
            <div
              key={`${result.type}-${result.id}`}
              onClick={() => handleSearchResultClick(result)}
              style={{
                padding: "10px 12px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                transition: "background-color 0.2s",
                hoverBackgroundColor: "rgba(59, 130, 246, 0.2)",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.2)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <span style={{ fontSize: "18px", marginRight: "10px" }}>
                {result.flag}
              </span>
              <div>
                <div style={{ color: "white", fontWeight: "medium" }}>
                  {result.name}
                </div>
                {result.type === "city" && (
                  <div
                    style={{
                      color: "rgba(255, 255, 255, 0.6)",
                      fontSize: "12px",
                    }}
                  >
                    {result.country}
                  </div>
                )}
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  backgroundColor:
                    result.type === "country"
                      ? "rgba(59, 130, 246, 0.2)"
                      : result.incidents === "red"
                      ? "rgba(239, 68, 68, 0.2)"
                      : result.incidents === "yellow"
                      ? "rgba(245, 158, 11, 0.2)"
                      : "rgba(16, 185, 129, 0.2)",
                  color:
                    result.type === "country"
                      ? "#60a5fa"
                      : result.incidents === "red"
                      ? "#ef4444"
                      : result.incidents === "yellow"
                      ? "#f59e0b"
                      : "#10b981",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: "medium",
                }}
              >
                {result.type === "country" ? "Country" : "Location"}
              </div>
            </div>
          ))
        )}
      </div>
    )}
  </div>
</div>

      {/* Loading indicator */}
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(16, 24, 40, 0.8)",
            zIndex: 1000,
            color: "white",
            fontSize: "18px",
            fontWeight: "bold",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "20px" }}>Loading Globe...</div>
            <div
              style={{
                width: "60px",
                height: "60px",
                border: "5px solid rgba(255, 255, 255, 0.3)",
                borderTop: "5px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <style>
              {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              `}
            </style>
          </div>
        </div>
      )}

      {/* Three.js container */}
      <div
        ref={mountRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />


      <div
        style={{
          position: "absolute",
          right: "20px",
          top: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          zIndex: 100,
        }}
      >
     
       
    <div
      style={{
        position: "absolute",
        right: "20px",
        top: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        zIndex: 100,
      }}
    >
     {!showSidePanel && (
      <button
        onClick={(e) => {
          // console.log("Reset View Button Clicked", e);
          handleResetView();
        }}
        style={{
          backgroundColor: "rgba(16, 24, 40, 0.8)",
          color: "white",
          border: "1px solid rgba(59, 130, 246, 0.5)",
          borderRadius: "8px",
         padding: "8px 12px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
          fontSize: "14px",
          fontWeight: "medium",
          backdropFilter: "blur(4px)",
        }}
      >
        🏠 Reset View
      </button>
     )}
      {/* Existing close side panel button */}
      {/* {showSidePanel && (
        <button
          onClick={handleCloseSidePanel}
          style={{
            backgroundColor: "rgba(16, 24, 40, 0.8)",
            color: "white",
            border: "1px solid rgba(59, 130, 246, 0.5)",
            borderRadius: "8px",
             padding: "8px 12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
            fontWeight: "medium",
            backdropFilter: "blur(4px)",
          }}
        >
          🌍 Back to Globe
        </button>
      )} */}
    </div>
    </div>
   
      {showSidePanel && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            width: "350px",
            backgroundColor: "rgba(13, 26, 48, 0.95)",
            borderLeft: "1px solid rgba(59, 130, 246, 0.3)",
            boxShadow: "-5px 0 15px rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(10px)",
            color: "white",
            zIndex: 90,
            overflowY: "auto",
            transition: "all 0.3s ease",
            padding: "15px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header with close button */}
       
{/* Header with close button */}
<div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    paddingBottom: "15px",
    borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
  }}
>
  <div style={{ display: "flex", alignItems: "center" }}>
    <span style={{ fontSize: "24px", marginRight: "10px" }}>
      {selectedCountryData?.flag}
    </span>
    <div>
      <div style={{ fontWeight: "bold", fontSize: "18px" }}>
        {selectedCountryData?.name}
      </div>
      <div
        style={{
          fontSize: "13px",
          color: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: "#3b82f6",
            marginRight: "8px",
          }}
        ></span>
        {selectedCountryData?.cities.length} location
        {selectedCountryData?.cities.length !== 1 ? "s" : ""}
      </div>
    </div>
  </div>
  <button
  onClick={handleResetView} // Simple direct handler
  style={{
    position: "absolute",
    top: "0",
    right: "0",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    border: "none",
    borderRadius: "50%",
    width: "40px", 
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "white",
    fontSize: "20px",
    zIndex: 1000,
    pointerEvents: "auto", // Explicitly enable pointer events
  }}
>
  ✕
</button>
</div>
          {/* City List - shown when a country is selected but no city is selected */}
          {selectedCountryData && !selectedCityData && (
  <div>
    <div 
      style={{
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '15px'
      }}
    >
      <div 
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          color: "#3b82f6",
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ marginRight: '8px' }}>+</span>
        Available Locations
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={handleResetView}
          style={{
            backgroundColor: "transparent",
            color: "white",
            border: "none",
            cursor: "pointer",
            display: 'flex',
            alignItems: 'center',
            fontSize: "14px",
            padding: '5px 10px',
          }}
        >
          🏠 Reset View
        </button>

        <button
          onClick={handleCloseSidePanel}
          style={{
            backgroundColor: "transparent",
            color: "white",
            border: "none",
            cursor: "pointer",
            display: 'flex',
            alignItems: 'center',
            fontSize: "14px",
            padding: '5px 10px',
          }}
        >
          🌍 Back to Globe
        </button>
      </div>
    </div>

    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
     {selectedCountryData.cities.map((city) => {
  // Existing city rendering logic
  const cityData = locationsWithDetails.find(
    (loc) => loc.id === city.id
  );
  if (!cityData) return null;

  return (
    <div
      key={city.id}
      onClick={() => showLocationDetails(cityData)}
      style={{
        backgroundColor: "rgba(16, 24, 40, 0.5)",
        borderRadius: "8px",
        padding: "15px",
        cursor: "pointer",
        border: "1px solid rgba(59, 130, 246, 0.2)",
        transition: "all 0.2s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontWeight: "bold", fontSize: "16px" }}>
          {city.name}
        </div>
        <div
          style={{
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "bold",
            backgroundColor: `${getStatusColor(
              cityData.incidents
            )}20`,
            color: getStatusColor(cityData.incidents),
            border: `1px solid ${getStatusColor(
              cityData.incidents
            )}40`,
          }}
        >
          {getStatusText(cityData.incidents)}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          marginTop: "12px",
          justifyContent: "space-between",
          color: "rgba(255, 255, 255, 0.7)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "5px" }}>⏱️</span>
          <span>{times[city.name] || "--:--"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "5px" }}>👥</span>
          <span>{city.details.employees} staff</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "5px" }}>⚠️</span>
          <span>{city.details.activeIncidents} issues</span>
        </div>
        {/* Sentiment indicator */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "5px" }}>😊</span>
          <span style={{ 
            color: getSentimentColor(cityData?.sentiment || "neutral")
          }}>
            {getSentimentText(cityData?.sentiment || "neutral")}
          </span>
        </div>
      </div>
    </div>
  );
})}
    </div>
  </div>
)}

          {/* City Details - shown when a city is selected */}
          {selectedCityData && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              {/* Back button */}
              <button
                onClick={() => setSelectedCityData(null)}
                style={{
                  backgroundColor: "rgba(16, 24, 40, 0.5)",
                  color: "white",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                  fontSize: "14px",
                }}
              >
                ← Back to {selectedCountryData?.name} Locations
              </button>

              {/* City header */}
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "rgba(16, 24, 40, 0.5)",
                  borderRadius: "8px",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontWeight: "bold", fontSize: "20px" }}>
                    {selectedCityData.name}
                  </div>

    {/* Add edit and delete buttons here */}
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {/* Edit button */}
      <button
        onClick={() => onEditLocation(selectedCityData)}
        style={{
          backgroundColor: "rgba(0, 229, 255, 0.1)",
          color: "#00e5ff",
          border: "none",
          borderRadius: "4px",
          padding: "5px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "28px",
          height: "28px"
        }}
      >
        <span role="img" aria-label="Edit">✏️</span>
      </button>
      
      {/* Delete button */}
      <button
        onClick={() => onDeleteLocation(selectedCityData.id)}
        style={{
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          color: "#ef4444",
          border: "none",
          borderRadius: "4px",
          padding: "5px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "28px",
          height: "28px"
        }}
      >
        <span role="img" aria-label="Delete">🗑️</span>
      </button>        <div
                    style={{
                      padding: "4px 10px",
                      borderRadius: "4px",
                      fontSize: "13px",
                      fontWeight: "bold",
                      backgroundColor: `${getStatusColor(
                        selectedCityData.incidents
                      )}20`,
                      color: getStatusColor(selectedCityData.incidents),
                      border: `1px solid ${getStatusColor(
                        selectedCityData.incidents
                      )}40`,
                    }}
                  >
                    {getStatusText(selectedCityData.incidents)}
                  </div>
                </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "10px",
                    fontSize: "15px",
                    color: "#3b82f6",
                  }}
                >
                  <span style={{ marginRight: "5px" }}>⏱️</span>
                  <span style={{ fontWeight: "bold" }}>
                    {times[selectedCityData.name] || "--:--"}
                  </span>
                  <span
                    style={{
                      marginLeft: "5px",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    {selectedCityData.timezoneName}
                  </span>
                </div>
              </div>

              {/* Key metrics */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "rgba(16, 24, 40, 0.5)",
                    borderRadius: "8px",
                    padding: "15px",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "rgba(255, 255, 255, 0.7)",
                      marginBottom: "5px",
                    }}
                  >
                    Staff
                  </div>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "22px",
                      color: "#3b82f6",
                    }}
                  >
                    {selectedCityData.details.employees}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "rgba(16, 24, 40, 0.5)",
                    borderRadius: "8px",
                    padding: "15px",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "rgba(255, 255, 255, 0.7)",
                      marginBottom: "5px",
                    }}
                  >
                    Active Incidents
                  </div>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "22px",
                      color: "#ef4444",
                    }}
                  >
                    {selectedCityData.details.activeIncidents}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "rgba(16, 24, 40, 0.5)",
                    borderRadius: "8px",
                    padding: "15px",
                    border: "1px solid rgba(16, 185, 129, 0.2)",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "rgba(255, 255, 255, 0.7)",
                      marginBottom: "5px",
                    }}
                  >
                    Resolved Incidents
                  </div>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "22px",
                      color: "#10b981",
                    }}
                  >
                    {selectedCityData.details.resolvedIncidents}
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: "rgba(16, 24, 40, 0.5)",
                    borderRadius: "8px",
                    padding: "15px",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "rgba(255, 255, 255, 0.7)",
                      marginBottom: "5px",
                    }}
                  >
                    Ongoing Initiatives
                  </div>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "22px",
                      color: "#3b82f6",
                    }}
                  >
                    {selectedCityData.details.ongoingInitiatives}
                  </div>
                </div>
              </div>
             
{/* Customer Sentiment Section */}
<div>
  <div
    style={{
      fontSize: "16px",
      fontWeight: "bold",
      marginBottom: "10px",
      color: "#3b82f6",
      display: "flex",
      alignItems: "center",
    }}
  >
    <span style={{ marginRight: "8px" }}>😊</span>
    Customer Sentiment Details
  </div>

  <div
    style={{
      backgroundColor: "rgba(16, 24, 40, 0.5)",
      borderRadius: "8px",
      padding: "15px",
      border: `1px solid ${getSentimentColor(selectedCityData?.sentiment || "neutral")}40`,
    }}
  >
    {/* Sentiment Header */}
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "15px",
      }}
    >
      <div style={{ fontWeight: "bold", fontSize: "16px" }}>
        Overall Customer Sentiment
      </div>
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "4px",
          fontSize: "14px",
          fontWeight: "bold",
          backgroundColor: `${getSentimentColor(selectedCityData?.sentiment || "neutral")}20`,
          color: getSentimentColor(selectedCityData?.sentiment || "neutral"),
          border: `1px solid ${getSentimentColor(selectedCityData?.sentiment || "neutral")}40`,
        }}
      >
        {getSentimentText(selectedCityData?.sentiment || "neutral")}
      </div>
    </div>

    {/* Sentiment Details */}
    <div style={{ marginBottom: "15px" }}>
      <div style={{ fontSize: "14px", color: "rgba(255, 255, 255, 0.7)" }}>
        {selectedCityData?.sentiment === "positive" ? (
          "Customers are generally satisfied with our services in this location. Positive feedback highlights excellent customer service and high product quality."
        ) : selectedCityData?.sentiment === "negative" ? (
          "Customers are expressing concerns in this location. Negative feedback indicates issues with service response times and product availability."
        ) : (
          "Customers have mixed reviews in this location. Feedback shows satisfaction with product quality but some concerns about pricing and availability."
        )}
      </div>
    </div>

    {/* Sentiment Metrics */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "10px",
        marginBottom: "15px",
      }}
    >
      {/* Generate fake sentiment metrics based on sentiment value */}
      <div
        style={{
          backgroundColor: "rgba(16, 24, 40, 0.3)",
          padding: "10px",
          borderRadius: "6px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.6)", marginBottom: "4px" }}>
          Satisfaction Score
        </div>
        <div style={{ fontWeight: "bold", fontSize: "18px" }}>
          {selectedCityData?.sentiment === "positive"
            ? "4.7/5.0"
            : selectedCityData?.sentiment === "negative"
            ? "2.3/5.0"
            : "3.6/5.0"}
        </div>
      </div>

      <div
        style={{
          backgroundColor: "rgba(16, 24, 40, 0.3)",
          padding: "10px",
          borderRadius: "6px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.6)", marginBottom: "4px" }}>
          NPS Score
        </div>
        <div style={{ fontWeight: "bold", fontSize: "18px" }}>
          {selectedCityData?.sentiment === "positive"
            ? "+42"
            : selectedCityData?.sentiment === "negative"
            ? "-12"
            : "+8"}
        </div>
      </div>

      <div
        style={{
          backgroundColor: "rgba(16, 24, 40, 0.3)",
          padding: "10px",
          borderRadius: "6px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.6)", marginBottom: "4px" }}>
          Feedback Count
        </div>
        <div style={{ fontWeight: "bold", fontSize: "18px" }}>
          {Math.floor(Math.random() * 200) + 50}
        </div>
      </div>
    </div>

    {/* Recent Customer Feedback */}
    <div>
      <div style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}>
        Recent Feedback
      </div>
      <div
        style={{
          backgroundColor: "rgba(16, 24, 40, 0.3)",
          borderRadius: "6px",
          marginBottom: "8px",
          padding: "10px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
          <div style={{ fontWeight: "bold", fontSize: "13px" }}>Customer #{Math.floor(Math.random() * 10000)}</div>
          <div
            style={{
              fontSize: "12px",
              color: selectedCityData?.sentiment === "positive"
                ? "#10b981"
                : selectedCityData?.sentiment === "negative"
                ? "#ef4444"
                : "#3b82f6",
            }}
          >
            {selectedCityData?.sentiment === "positive"
              ? "★★★★★"
              : selectedCityData?.sentiment === "negative"
              ? "★★☆☆☆"
              : "★★★☆☆"}
          </div>
        </div>
        <div style={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.8)" }}>
          {selectedCityData?.sentiment === "positive"
            ? `"Excellent service and product quality. The team at ${selectedCityData?.name} went above and beyond."`
            : selectedCityData?.sentiment === "negative"
            ? `"Disappointed with response times and product availability at the ${selectedCityData?.name} location."`
            : `"Good products but pricing could be more competitive at ${selectedCityData?.name}."`}
        </div>
      </div>
      <div
        style={{
          backgroundColor: "rgba(16, 24, 40, 0.3)",
          borderRadius: "6px",
          padding: "10px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
          <div style={{ fontWeight: "bold", fontSize: "13px" }}>Customer #{Math.floor(Math.random() * 10000)}</div>
          <div
            style={{
              fontSize: "12px",
              color: selectedCityData?.sentiment === "positive"
                ? "#10b981"
                : selectedCityData?.sentiment === "negative"
                ? "#ef4444"
                : "#3b82f6",
            }}
          >
            {selectedCityData?.sentiment === "positive"
              ? "★★★★☆"
              : selectedCityData?.sentiment === "negative"
              ? "★★★☆☆"
              : "★★★★☆"}
          </div>
        </div>
        <div style={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.8)" }}>
          {selectedCityData?.sentiment === "positive"
            ? `"Very satisfied with my recent purchase. Staff was helpful and knowledgeable."`
            : selectedCityData?.sentiment === "negative"
            ? `"Products are good but customer service needs improvement in this location."`
            : `"Decent experience overall. Some products were out of stock but staff was helpful."`}
        </div>
      </div>
    </div>

    {/* Action Button */}
    <button
      style={{
        backgroundColor: `${getSentimentColor(selectedCityData?.sentiment || "neutral")}20`,
        color: getSentimentColor(selectedCityData?.sentiment || "neutral"),
        border: `1px solid ${getSentimentColor(selectedCityData?.sentiment || "neutral")}40`,
        borderRadius: "6px",
        padding: "10px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        marginTop: "15px",
      }}
    >
      {selectedCityData?.sentiment === "positive"
        ? "📊 View Detailed Feedback Analysis"
        : selectedCityData?.sentiment === "negative"
        ? "🔍 View Improvement Action Plan"
        : "📈 View Sentiment Trend Analysis"}
    </button>
  </div>
</div>

              {/* Milestones Section */}
              <div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginBottom: "10px",
                    color: "#3b82f6",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span style={{ marginRight: "8px" }}>🚩</span>
                  Upcoming Milestones
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {selectedCityData.milestones &&
                    selectedCityData.milestones.map((milestone) => (
                      <div
                        key={milestone.id}
                        style={{
                          backgroundColor: "rgba(16, 24, 40, 0.5)",
                          borderRadius: "8px",
                          padding: "15px",
                          border: "1px solid rgba(59, 130, 246, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <div style={{ fontWeight: "bold", fontSize: "15px" }}>
                            {milestone.name}
                          </div>
                          <div
                            style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              backgroundColor: `${milestone.statusColor}20`,
                              color: milestone.statusColor,
                              border: `1px solid ${milestone.statusColor}40`,
                            }}
                          >
                            {milestone.status}
                          </div>
                        </div>

                        <div
                          style={{
                            fontSize: "14px",
                            color: "rgba(255, 255, 255, 0.7)",
                            margin: "8px 0",
                          }}
                        >
                          {milestone.description}
                        </div>

                        <div
                          style={{
                            fontSize: "13px",
                            color: "rgba(255, 255, 255, 0.5)",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ marginRight: "5px" }}>📅</span>
                          Due: {milestone.dueDate}
                        </div>
                      </div>
                    ))}

                  {(!selectedCityData.milestones ||
                    selectedCityData.milestones.length === 0) && (
                    <div
                      style={{
                        backgroundColor: "rgba(16, 24, 40, 0.5)",
                        borderRadius: "8px",
                        padding: "15px",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        textAlign: "center",
                        color: "rgba(255, 255, 255, 0.5)",
                      }}
                    >
                      No upcoming milestones
                    </div>
                  )}
                </div>
              </div>

              {/* Risks Section */}
              <div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginBottom: "10px",
                    color: "#3b82f6",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span style={{ marginRight: "8px" }}>⚠️</span>
                  Active Risks
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {selectedCityData.risks &&
                    selectedCityData.risks.map((risk) => (
                      <div
                        key={risk.id}
                        style={{
                          backgroundColor: "rgba(16, 24, 40, 0.5)",
                          borderRadius: "8px",
                          padding: "15px",
                          border: "1px solid rgba(59, 130, 246, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <div style={{ fontWeight: "bold", fontSize: "15px" }}>
                            {risk.name}
                          </div>
                          <div
                            style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              backgroundColor: `${risk.severityColor}20`,
                              color: risk.severityColor,
                              border: `1px solid ${risk.severityColor}40`,
                            }}
                          >
                            {risk.severity}
                          </div>
                        </div>

                        <div
                          style={{
                            fontSize: "14px",
                            color: "rgba(255, 255, 255, 0.7)",
                            margin: "8px 0",
                          }}
                        >
                          {risk.description}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: "13px",
                            color: "rgba(255, 255, 255, 0.5)",
                          }}
                        >
                          <div>Impact: {risk.impact}</div>
                          <div>{risk.mitigationPlan}</div>
                        </div>
                      </div>
                    ))}

                  {(!selectedCityData.risks ||
                    selectedCityData.risks.length === 0) && (
                    <div
                      style={{
                        backgroundColor: "rgba(16, 24, 40, 0.5)",
                        borderRadius: "8px",
                        padding: "15px",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        textAlign: "center",
                        color: "rgba(255, 255, 255, 0.5)",
                      }}
                    >
                      No active risks
                    </div>
                  )}
                </div>
              </div>

              {/* Location Info */}
              <div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginBottom: "10px",
                    color: "#3b82f6",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span style={{ marginRight: "8px" }}>📍</span>
                  Location Information
                </div>

                <div
                  style={{
                    backgroundColor: "rgba(16, 24, 40, 0.5)",
                    borderRadius: "8px",
                    padding: "15px",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                  }}
                >
                  <div style={{ marginBottom: "10px" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      Coordinates
                    </div>
                    <div style={{ fontSize: "15px" }}>
                      {selectedCityData.lat.toFixed(4)}°,{" "}
                      {selectedCityData.lng.toFixed(4)}°
                    </div>
                  </div>

                  <div style={{ marginBottom: "10px" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      Timezone
                    </div>
                    <div style={{ fontSize: "15px" }}>
                      {selectedCityData.timezone} (
                      {selectedCityData.timezoneName})
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "rgba(255, 255, 255, 0.7)",
                      }}
                    >
                      Office ID
                    </div>
                    <div style={{ fontSize: "15px" }}>
                      {selectedCityData.id}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Actions */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                  marginTop: "10px",
                }}
              >
                <button
                  style={{
                    flex: 1,
                    backgroundColor: "rgba(59, 130, 246, 0.2)",
                    color: "#3b82f6",
                    border: "1px solid rgba(59, 130, 246, 0.5)",
                    borderRadius: "6px",
                    padding: "10px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  📞 Contact Office
                </button>

                <button
                  style={{
                    flex: 1,
                    backgroundColor: "rgba(16, 185, 129, 0.2)",
                    color: "#10b981",
                    border: "1px solid rgba(16, 185, 129, 0.5)",
                    borderRadius: "6px",
                    padding: "10px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  📊 View Reports
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Location Info Tooltip */}
      {hoveredLocation && !selectedCityData && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            bottom: "20px",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(16, 24, 40, 0.95)",
            color: "white",
            padding: "16px",
            borderRadius: "8px",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            maxWidth: "350px",
            zIndex: 1000,
            width: "90%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              paddingBottom: "10px",
            }}
          >
            <span style={{ fontSize: "1.5rem", marginRight: "10px" }}>
              {hoveredLocation.flag}
            </span>
            <div>
              <div
                style={{ fontWeight: "bold", color: "white", fontSize: "17px" }}
              >
                {hoveredLocation.name}
              </div>
              <div
                style={{ fontSize: "14px", color: "rgba(255, 255, 255, 0.7)" }}
              >
                {hoveredLocation.country}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: "#60a5fa",
              fontSize: "15px",
              marginBottom: "12px",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#60a5fa",
                marginRight: "8px",
              }}
            ></span>
            <span style={{ fontWeight: "bold" }}>
              {times[hoveredLocation.name] || "--:--"}
            </span>
            <span
              style={{ marginLeft: "5px", color: "rgba(255, 255, 255, 0.7)" }}
            >
              {hoveredLocation.timezoneName}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: "15px",
            }}
          >
            <div
              style={{
                flex: 1,
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                padding: "10px",
                borderRadius: "6px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.6)",
                  marginBottom: "5px",
                }}
              >
                Employees
              </div>
              <div style={{ fontWeight: "bold", fontSize: "18px" }}>
                {hoveredLocation.details.employees}
              </div>
            </div>

            <div
              style={{
                flex: 1,
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                padding: "10px",
                borderRadius: "6px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.6)",
                  marginBottom: "5px",
                }}
              >
                Incidents
              </div>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color:
                    hoveredLocation.incidents === "red"
                      ? "#ef4444"
                      : hoveredLocation.incidents === "yellow"
                      ? "#f59e0b"
                      : "#10b981",
                }}
              >
                {hoveredLocation.details.activeIncidents}
              </div>
            </div>
            <div
  style={{
    backgroundColor: "rgba(16, 24, 40, 0.5)",
    borderRadius: "8px",
    padding: "15px",
    border: `1px solid ${getSentimentColor(selectedCityData?.sentiment || "neutral")}40`,
    textAlign: "center",
  }}
>
  <div
    style={{
      fontSize: "13px",
      color: "rgba(255, 255, 255, 0.7)",
      marginBottom: "5px",
    }}
  >
    Customer Sentiment
  </div>
  <div
    style={{
      fontWeight: "bold",
      fontSize: "22px",
      color: getSentimentColor(selectedCityData?.sentiment || "neutral"),
    }}
  >
    {getSentimentText(selectedCityData?.sentiment || "neutral")}
  </div>
</div>


            <div
              style={{
                flex: 1,
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                padding: "10px",
                borderRadius: "6px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.6)",
                  marginBottom: "5px",
                }}
              >
                Initiatives
              </div>
              <div style={{ fontWeight: "bold", fontSize: "18px" }}>
                {hoveredLocation.details.ongoingInitiatives}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                padding: "5px 10px",
                borderRadius: "6px",
                backgroundColor:
                  hoveredLocation.incidents === "red"
                    ? "rgba(239, 68, 68, 0.15)"
                    : hoveredLocation.incidents === "yellow"
                    ? "rgba(245, 158, 11, 0.15)"
                    : "rgba(16, 185, 129, 0.15)",
                color:
                  hoveredLocation.incidents === "red"
                    ? "#ef4444"
                    : hoveredLocation.incidents === "yellow"
                    ? "#f59e0b"
                    : "#10b981",
                fontSize: "13px",
                fontWeight: "bold",
                border: `1px solid ${
                  hoveredLocation.incidents === "red"
                    ? "rgba(239, 68, 68, 0.3)"
                    : hoveredLocation.incidents === "yellow"
                    ? "rgba(245, 158, 11, 0.3)"
                    : "rgba(16, 185, 129, 0.3)"
                }`,
              }}
            >
              {hoveredLocation.incidents === "red"
                ? "High Alert"
                : hoveredLocation.incidents === "yellow"
                ? "Medium Alert"
                : "Normal Status"}
            </div>

            <div
              style={{
                marginLeft: "auto",
                fontSize: "13px",
                color: "rgba(255, 255, 255, 0.5)",
              }}
            >
              {hoveredLocation.lat.toFixed(2)}°,{" "}
              {hoveredLocation.lng.toFixed(2)}°
            </div>
          </div>

          <div
            style={{
              marginTop: "12px",
              textAlign: "center",
              fontSize: "13px",
              color: "rgba(255, 255, 255, 0.6)",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              padding: "8px",
              borderRadius: "4px",
              border: "1px dashed rgba(59, 130, 246, 0.3)",
            }}
          >
            Click to view detailed information
          </div>
        </div>
      )}

      {/* Help text - only shown when no location is hovered */}
      {!hoveredLocation && !selectedCountry && !showSidePanel && (
        <div
          style={{
            position: "absolute",
            right: "20px",
            bottom: "20px",
            backgroundColor: "rgba(16, 24, 40, 0.9)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: "8px",
            padding: "12px 15px",
            color: "white",
            maxWidth: "280px",
            zIndex: 10,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              marginBottom: "10px",
              fontSize: "15px",
            }}
          >
            Interactive Global Operations Map
          </div>
          <div
            style={{
              opacity: 0.9,
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#60a5fa",
                marginRight: "8px",
              }}
            ></div>
            Drag to rotate the globe
          </div>
          <div
            style={{
              opacity: 0.9,
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#60a5fa",
                marginRight: "8px",
              }}
            ></div>
            Click countries for details
          </div>
          <div
            style={{
              opacity: 0.9,
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#60a5fa",
                marginRight: "8px",
              }}
            ></div>
            Scroll to zoom in/out
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobeVisualization;
GlobeVisualization.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
      countryCode: PropTypes.string.isRequired,
      flag: PropTypes.string,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      timezone: PropTypes.string,
      timezoneName: PropTypes.string,
      incidents: PropTypes.oneOf(["red", "yellow", "green"]),
      details: PropTypes.shape({
        employees: PropTypes.number,
        activeIncidents: PropTypes.number,
        resolvedIncidents: PropTypes.number,
        ongoingInitiatives: PropTypes.number,
        completedInitiatives: PropTypes.number,
      }),
      milestones: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          description: PropTypes.string,
          dueDate: PropTypes.string,
          status: PropTypes.string,
          statusColor: PropTypes.string,
        })
      ),
      risks: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          description: PropTypes.string,
          severity: PropTypes.string,
          severityColor: PropTypes.string,
          impact: PropTypes.string,
          mitigationPlan: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  onLocationSelect: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  currentTime: PropTypes.instanceOf(Date),
  onEditLocation: PropTypes.func,
  onDeleteLocation: PropTypes.func
};
